import {EzLayout, EzChip, EzTextStyle} from '@ezcater/recipe';
import {formatOrderNumber} from 'util/StringUtils';
import {format} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import type {DriverAssignmentsQuery} from 'services/graphql/types';
import {DmDeliveryStatus} from 'services/graphql/types';
import dateToTimeFormatter from 'services/dateToTimeFormatter';
import {
  CalendarCard,
  CardLink,
  Month,
  DayOfMonth,
  OrderNumber,
  RightSide,
} from './DeliveryCard.styles';

type DriverAssignment = DriverAssignmentsQuery['driverAssignments'][number];

interface DeliveryCardProps {
  assignment: DriverAssignment;
}

const success = new Set([DmDeliveryStatus.Completed, DmDeliveryStatus.Expired]);
const neutral = new Set([DmDeliveryStatus.Assigned]);

function pillColor(status: DmDeliveryStatus) {
  if (success.has(status)) {
    return 'success' as const;
  }
  if (neutral.has(status)) {
    return 'common.grey120' as const;
  }
  return 'info' as const;
}

const DeliveryCard = ({assignment}: DeliveryCardProps) => {
  const {t} = useTranslation();
  const {delivery} = assignment;
  const deliveryStatus = delivery.status || DmDeliveryStatus.Assigned;

  const eventTime = new Date(delivery.eventAt);
  const history = useHistory();

  const statusPillColor = pillColor(deliveryStatus);

  const cardClick = () => {
    history.push(`/assignment/${assignment.id}`);
  };

  return (
    <CardLink onClick={cardClick}>
      <EzLayout layout="split">
        <EzLayout>
          <EzLayout layout="stack">
            <CalendarCard>
              <Month>{format(eventTime, 'E').toUpperCase()}</Month>
              <DayOfMonth>
                {format(eventTime, 'LLL').toUpperCase()} {eventTime.getDate()}
              </DayOfMonth>

              <EzTextStyle align="center">
                {dateToTimeFormatter(eventTime, delivery.pickupAddress.timeZoneIdentifier)}
              </EzTextStyle>
            </CalendarCard>
          </EzLayout>
          <EzLayout layout="stack">
            <div>
              <OrderNumber>{formatOrderNumber(delivery.orderNumber)}</OrderNumber>
            </div>
            <div>
              <p>{delivery.dropoffAddress.street1}</p>
              {delivery.dropoffAddress.street2 && <p>{delivery.dropoffAddress.street2}</p>}
              <p>
                {delivery.dropoffAddress.city},&nbsp;
                {delivery.dropoffAddress.state}&nbsp;
                {delivery.dropoffAddress.zip}
              </p>
            </div>
            {deliveryStatus !== DmDeliveryStatus.Unassigned && (
              <div data-testid="delivery-status-text">
                <EzChip
                  label={t(`driverDeliveryStatuses.${deliveryStatus}`)}
                  size="inherit"
                  color={statusPillColor}
                />
              </div>
            )}
          </EzLayout>
        </EzLayout>
        <RightSide />
      </EzLayout>
    </CardLink>
  );
};

export default DeliveryCard;
